// Entitlement tier (F11, F13, F15). Hydrated from the stored licence; re-validated weekly when online.
import { signal } from '@preact/signals';
import { activate as activateKey, deactivate as deactivateKey, validate, type Entitlement, type LicenceResult } from './licence';
import { needsRevalidation, withinGrace } from './policy';

export type Tier = 'none' | 'supporter' | 'school';
export const tier = signal<Tier>('none');
export const entitlement = signal<Entitlement | null>(null);
/** Shown in the unlock dialog after a lapse or a failed check. */
export const licenceNotice = signal<string | null>(null);

/** School licences include every supporter perk. */
export function canAccess(required: 'free' | 'supporter' | 'school'): boolean {
  if (required === 'free') return true;
  if (required === 'supporter') return tier.value === 'supporter' || tier.value === 'school';
  return tier.value === 'school';
}

// Local storage holds only the entitlement (allowed by CLAUDE.md): no identifiers beyond the key the reader pasted.
const STORAGE_KEY = 'marrow.entitlement';

function save(e: Entitlement | null) {
  try {
    if (e) localStorage.setItem(STORAGE_KEY, JSON.stringify(e));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode: the unlock lasts for this visit */
  }
}

function load(): Entitlement | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Entitlement) : null;
  } catch {
    return null;
  }
}

function apply(e: Entitlement | null) {
  entitlement.value = e;
  tier.value = e?.tier ?? 'none';
  save(e);
}

/**
 * Start-up: restore the stored licence, honour the 30-day offline grace, and re-check weekly when online.
 * A lapsed licence turns perks off; downloaded offline data is left alone (the reader removes it if they want).
 */
export async function initEntitlement(now = Date.now(), online = typeof navigator === 'undefined' || navigator.onLine): Promise<void> {
  const e = load();
  if (!e) return;
  const inGrace = withinGrace(e.validatedAt, now);
  entitlement.value = e;
  tier.value = inGrace ? e.tier : 'none';
  if (!online || !needsRevalidation(e.validatedAt, now)) return;
  const r = await validate(e, now);
  if (r.ok) {
    apply(r.entitlement);
    licenceNotice.value = null;
  } else if (r.reason !== 'network') {
    apply(null);
    licenceNotice.value = r.message;
  }
}

export async function unlock(key: string): Promise<LicenceResult> {
  const r = await activateKey(key);
  if (r.ok) {
    apply(r.entitlement);
    licenceNotice.value = null;
  }
  return r;
}

/** Frees this device's activation slot and forgets the licence here. */
export async function removeFromDevice(): Promise<void> {
  const e = entitlement.value;
  if (e) await deactivateKey(e);
  apply(null);
}
